import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import { applyPublicationSearchCriteria } from "domains/publication/actions";
import { KnowledgeDetail } from "domains/publication/components/KnowledgeDetail";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import {
  createPublicationsForTopicSelector,
  getIsPublicationFilterCount,
  getIsPublicationFiltered,
  getPublicationSearchTerm,
} from "domains/publication/selectors";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";
import { getCanSeeSectionContent } from "entities/permissions/selectors";
import { fetchTopic } from "entities/topics/actions";
import {
  createHasUnauthenticatedContentAccessSelector,
  createTopicSelector,
  getTopic,
} from "entities/topics/selectors";

export const TopicPublicationPage = () => {
  const dispatch = useDispatch();
  const { keyIssueId, knowledgeId, topicId } = useParams();
  const hasAccessToContent = useSelector(getCanSeeSectionContent);
  const filterCount = useSelector(getIsPublicationFilterCount);
  const isFiltered = useSelector(getIsPublicationFiltered);
  const searchTerm = useSelector(getPublicationSearchTerm);
  const { hasFilters } = useTopicContentMeta("publications");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicPublicationPage");
  const selectedTopicId = keyIssueId ?? topicId;

  const publicationsForTopic = useSelector(createPublicationsForTopicSelector(selectedTopicId));
  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const keyIssue = useSelector(getTopic(keyIssueId));
  const topic = useSelector(createTopicSelector(topicId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(publicationsForTopic?.data ?? []),
    [publicationsForTopic?.data],
  );

  const publications = useSelector(knowledgesSelector);

  const id = getPageId(knowledgeId, publications);

  const knowledge = useSelector(getKnowledgeById(id));

  const hasNextPage = publicationsForTopic?.hasNextPage ?? false;
  const isPending = publicationsForTopic?.isFetching ?? false;
  const isLocked = !hasAccessToContent && !unauthenticatedSectionAccess;
  const selectedTopic = keyIssueId ? keyIssue : topic;

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Publications List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !knowledge) {
      return;
    }
    const event = getTopicRelatedItemDetailPageEvent(
      "Knowledge detail",
      { id: knowledge.id, name: knowledge.title },
      "Knowledge",
      selectedTopic,
    );
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledge?.id, selectedTopic?.id, track]);

  useEffect(() => {
    if (publications.length === 0 || isPending) {
      dispatch(
        applyPublicationSearchCriteria({
          topicId: selectedTopicId,
          offset: 0,
          limit: 20,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId, hasAccessToContent]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  const getLink = (knowledgeId: string) => `${path}/publications/${knowledgeId}`;

  const loadNextPage = () => {
    dispatch(
      applyPublicationSearchCriteria({
        topicId: selectedTopicId,
        offset: publications.length,
        limit: 20,
      }),
    );
  };

  let emptyMessage = isFiltered
    ? translate("app.monitor.empty_message_filter")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.knowledge.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  const knowledgeList = (
    <KnowledgeList
      items={publications}
      selectedId={id}
      trackPage="Publications List"
      getToLink={getLink}
      type="medium"
      loadNextPage={!isLocked ? loadNextPage : undefined}
      hasNextPage={!isLocked ? hasNextPage : false}
      isPending={isPending}
      lockTiles={isLocked}
      emptyMessage={emptyMessage}
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}/topics/${topicId}/publications/${knowledge ? knowledge.id : ""}`;

  const title = translate("app.discover.topic.tabs.knowledge.title");

  const onFilterClick = () =>
    dispatch(activateModal({ id: "TopicPublicationFilter", params: { selectedTopicId, topicId } }));

  const topicFilterButton =
    topic?.id && !isLocked && hasFilters ? <FilterButton onClick={onFilterClick} count={filterCount} /> : undefined;

  const onSearchHandle = (search: string) =>
    dispatch(applyPublicationSearchCriteria({ topicId: selectedTopicId, search }));

  const searchElement =
    topic?.id && !isLocked ? (
      <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={searchTerm} />
    ) : undefined;

  return (
    <MasterDetailLayout
      listTitle={title}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.knowledge.title"),
        path: `${path}/publications`,
      }}
      mainView={knowledgeId ? "right" : "left"}
      listHeaderRightElement={topicFilterButton}
      headerElement={searchElement}
    >
      <KnowledgeDetail
        id={id}
        knowledge={knowledge}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.knowledge.title" }}
      />
    </MasterDetailLayout>
  );
};
